type JsonRecord = Record<string, unknown>;

export interface ComposerDraftPersistenceStorage {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => void;
  readonly removeItem: (key: string) => void;
}

const PERSISTED_MAP_KEYS = [
  "draftsByThreadKey",
  "draftThreadsByThreadKey",
  "logicalProjectDraftThreadKeyByLogicalProjectKey",
] as const;

const PERSISTED_VALUE_KEYS = ["stickyModelSelectionByProvider", "stickyActiveProvider"] as const;

interface PersistedComposerDraftStorage {
  readonly version: number;
  readonly state: JsonRecord;
}

type MigrationValidator = (raw: string | null, version: number) => boolean;

export function mergeComposerDraftStorageWrite(input: {
  readonly previousLocal: string | null;
  readonly currentStored: string | null;
  readonly nextLocal: string;
  readonly version: number;
  readonly canMigrate?: MigrationValidator;
}): string {
  const nextLocal = decodeStorage(input.nextLocal, input.version, false);
  if (!nextLocal) return input.currentStored ?? input.nextLocal;
  if (
    input.currentStored !== null &&
    input.canMigrate !== undefined &&
    !input.canMigrate(input.currentStored, input.version)
  ) {
    return input.currentStored;
  }
  // The first post-hydration write may migrate a legacy or incomplete value.
  // When no other tab has changed that raw value, the migrated payload is safe.
  if (
    input.previousLocal === input.currentStored &&
    (input.canMigrate ?? isKnownMigrationInput)(input.currentStored, input.version)
  ) {
    return input.nextLocal;
  }
  const previousLocal = decodeStorage(input.previousLocal, input.version, true);
  const currentStored = decodeStorage(input.currentStored, input.version, true);
  if (!currentStored) return input.currentStored ?? input.nextLocal;
  if (!previousLocal) return input.currentStored ?? input.nextLocal;

  const state = { ...currentStored.state };
  for (const key of PERSISTED_MAP_KEYS) {
    state[key] = mergeChangedMap(
      previousLocal.state[key] as JsonRecord,
      currentStored.state[key] as JsonRecord,
      nextLocal.state[key] as JsonRecord,
    );
  }
  for (const key of PERSISTED_VALUE_KEYS) {
    if (!sameValue(previousLocal.state[key], nextLocal.state[key])) {
      state[key] = nextLocal.state[key];
    }
  }
  return JSON.stringify({ ...currentStored, version: input.version, state });
}

function isKnownMigrationInput(raw: string | null, version: number): boolean {
  if (raw === null) return true;
  try {
    const value = JSON.parse(raw) as unknown;
    if (
      !isRecord(value) ||
      !isRecord(value.state) ||
      typeof value.version !== "number" ||
      !Number.isInteger(value.version)
    ) {
      return false;
    }
    if (value.version >= 1 && value.version < version) return true;
    return false;
  } catch {
    return false;
  }
}

function decodeStorage(
  raw: string | null,
  version: number,
  allowEmpty: boolean,
): PersistedComposerDraftStorage | null {
  if (raw === null) return allowEmpty ? { version, state: emptyState() } : null;
  const value = parseJsonRecord(raw);
  return value && hasCurrentStorageShape(value, version) ? { version, state: value.state } : null;
}

function parseJsonRecord(raw: string): JsonRecord | null {
  try {
    const value = JSON.parse(raw) as unknown;
    return isRecord(value) ? value : null;
  } catch {
    return null;
  }
}

function hasCurrentStorageShape(
  value: JsonRecord,
  version: number,
): value is JsonRecord & { readonly state: JsonRecord } {
  const state = value.state;
  if (value.version !== version || !isRecord(state)) return false;
  return PERSISTED_MAP_KEYS.every((key) => isRecord(state[key]));
}

function emptyState(): JsonRecord {
  return Object.fromEntries(PERSISTED_MAP_KEYS.map((key) => [key, {}]));
}

function mergeChangedMap(previous: JsonRecord, current: JsonRecord, next: JsonRecord): JsonRecord {
  const merged = { ...current };
  for (const key of new Set([...Object.keys(previous), ...Object.keys(next)])) {
    if (sameValue(previous[key], next[key])) continue;
    if (key in next) merged[key] = next[key];
    else delete merged[key];
  }
  return merged;
}

function sameValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function createComposerDraftPersistenceStorage(input: {
  readonly storage: ComposerDraftPersistenceStorage;
  readonly version: number;
  readonly canMigrate?: MigrationValidator;
}): ComposerDraftPersistenceStorage {
  let previousLocal: string | null | undefined;
  return {
    getItem: (key) => {
      const value = input.storage.getItem(key);
      if (previousLocal === undefined) previousLocal = value;
      return value;
    },
    setItem: (key, nextLocal) => {
      const currentStored = input.storage.getItem(key);
      const merged = mergeComposerDraftStorageWrite({
        previousLocal: previousLocal ?? null,
        currentStored,
        nextLocal,
        version: input.version,
        ...(input.canMigrate === undefined ? {} : { canMigrate: input.canMigrate }),
      });
      if (merged === currentStored && currentStored !== nextLocal) return;
      input.storage.setItem(key, merged);
      previousLocal = nextLocal;
    },
    removeItem: (key) => {
      input.storage.removeItem(key);
      previousLocal = null;
    },
  };
}
